import { DateTime } from 'luxon';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, SirenActionMixin, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-calendar';
import '../../common/bn-icon';
import '../../common/bn-loading';
import './scheduler-locations';
import './scheduler-map';
import './scheduler-work-orders';

class SchedulerUser extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.allLocations = [];
    this.locationsByDate = new Map();
  }

  static get properties() {
    return {
      locationsByDate: { type: Map, attribute: false },
      locationsHref: { type: String, attribute: false },
      selectedLocationHref: { type: String, attribute: false },
      selectedLocations: { type: Array, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      h1, h2 {
        margin-left: .5rem;
      }
      .user-name {
        color: var(--gray-80);
      }
      .summary {
        display: flex;
        flex-wrap: wrap;
        border: 1px solid var(--blue-50);
        background-color: var(--white-base);
        align-items: center;
        justify-content: center;
        font-size: .8em;
      }
      .summary .item {
        margin: .2em .5em;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-around;
      }
      .summary bn-icon {
        margin-right: .3em;
        color: var(--primary);
      }
      .summary .count {
        text-align: right;
        width: 1em;
        text-align: center;
      }
      .summary .farm {
        color: var(--primary);
        font-weight: 600;
      }
      .panels {
        display: flex;
        align-items: flex-start;
      }
      .panel.one {
        width: 40%;
      }
      .panel.two {
        width: 60%;
        margin-left: 1em;
        position: relative;
      }
      scheduler-work-orders {
        position: absolute;
        top: .5rem;
        right: .5rem;
        left: .5rem;
        background-color: var(--surface);
        border: 1px solid var(--gray-50);
        box-shadow: 0 0 6px rgba(0, 0, 0, .3);
        padding: .5rem;
      }
      @media(max-width: 1000px) {
        .panels {
          flex-direction: column;
        }
        .panel.one {
          width: 100%;
        }
        .panel.two {
          width: 100%;
          margin: 0;
        }
      }
      @media(max-width: 400px) {
        .summary {
          margin: 0 -.3em;
        }
        .summary bn-icon {
          margin: 0;
        }
        .item {
          justify-content: center;
        }
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`<bn-loading></bn-loading>`;
    }

    const daySummaries = [];
    if (this.locationsByDate) {
      for (const [scheduledFor, locations] of this.locationsByDate) {
        daySummaries.push(html`
          <div slot="${scheduledFor}" class="summary">
            <div class="item farm">
              <bn-icon name="fad fa-farm"></bn-icon>
              <span class="count">${locations.length}</span>
            </div>
          </div>
        `);
      }
    }

    return html`
      <div class="panels">
        <div class="panel one">
          <h1>Scheduler: <span class="user-name">${this.getProperty('name')}</span></h1>
          <bn-calendar @select="${this._onSelectDate}">
            ${daySummaries}
          </bn-calendar>
          <div class="locations">
            <h2>Work Order Locations</h2>
            <scheduler-locations
              .locations="${this.selectedLocations}"
              @response="${this._reload}"
              @select="${this._onSelectLocation}"
            ></scheduler-locations>
          </div>
        </div>
        <div class="panel two">
          <scheduler-map .locations="${this.selectedLocations}"></scheduler-map>
          ${renderIf(this.selectedLocationHref, () => html`
            <scheduler-work-orders .href="${this.selectedLocationHref}" @close="${this._unselect}"></scheduler-work-orders>
          `)}
        </div>
      </div>
      <lit-entity .href="${this.locationsHref}" @update="${this._onUpdateLocations}"></lit-entity>
    `;
  }

  _onSelectLocation(e) {
    const { location } = e.detail;
    this.selectedLocationHref = location && this.getHref('self', location);
  }

  _unselect() {
    this.selectedLocationHref = undefined;
  }

  _reload() {
    entityStore.invalidateHref(this.locationsHref);
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.locationsHref = this.getHref('https://api.binsentry.com/rel/work-order-locations');
  }

  async _onUpdateLocations(e) {
    const { entity } = e.detail;

    if (!entity) {
      this.locationsByDate = new Map();
      return;
    }

    this.allLocations = entity.getSubEntitiesByClass('work-order-location');

    let nextEntity = entity;
    while (nextEntity.hasLink('next')) {
      nextEntity = await entityStore.fetchWait(nextEntity.getLink('next').href);
      this.allLocations.push(...nextEntity.getSubEntitiesByClass('work-order-location'));
    }

    this.locationsByDate = this.allLocations.reduce((map, locationEntity) => {
      const { scheduledFor } = locationEntity.properties;
      let a = map.get(scheduledFor);
      if (!a) {
        map.set(scheduledFor, a = []);
      }
      a.push(locationEntity);
      return map;
    }, new Map());

    this._updateDate();
  }

  _onSelectDate(e) {
    this.selectedDate = e.detail.date;
    this.selectedLocationHref = undefined;
    this._updateDate();
  }

  _updateDate() {
    if (!this.selectedDate) {
      return;
    }

    const scheduledFor = this.selectedDate.toISODate();
    const today = DateTime.local().toFormat('yyyy-MM-dd');
    const isToday = this.selectedDate.hasSame(DateTime.local(), 'day');

    const previousLocations = isToday ? this.allLocations.filter(l => l.properties.scheduledFor < today) : [];
    this.selectedLocations = [...previousLocations, ...(this.locationsByDate.get(scheduledFor) || [])];
  }
}

customElements.define('scheduler-user', SchedulerUser);
